import { Route, BrowserRouter, Routes, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import ROUTES from "./routes";
import CheckLogin from "../pages/Layout/CheckLogin";
import Login from "../pages/Login";
import ProtectedRoute from "../pages/Layout/ProtectedRoute";
import AppLayout from "../pages/Layout/AppLayout";
import Chat from "../pages/Chat";
import SpectraDash from "../pages/Spectra/SpectraDash";
import SparksDash from "../pages/Spark/SparksDash";
import CreateSpectraPage from "../pages/Spectra/CreateSpectra";
import EditSpectraPage from "../pages/Spectra/EditSpectra";
import SpectraLandingPage from "../pages/Spectra/SpectraLandingPage";
import SpectraChatPage from "../pages/Spectra/SpectraChatPage";
import ForgeDashboard from "../pages/Forge/ForgeDashboard";
import AddProviderPage from "../pages/Forge/AddProviderPage";
import AddAgentPage from "../pages/Forge/AddAgentPage";
import EditProviderPage from "../pages/Forge/EditProviderPage";
import EditAgentPage from "../pages/Forge/EditAgentPage";
import HandleInvitation from "../pages/Spectra/HandleInvitation";
import ActionPage from "../pages/Auth/ActionPage";
import BrowserAuthorizationPage from "../pages/Auth/BrowserAuthorizationPage";
import ModelListingPage from "../pages/Settings/ModelListingPage";
import ModelGardenLayout, { ModelGardenIndexRedirect } from "../pages/Settings/ModelGardenLayout";
import ModelConfigPage from "../pages/Settings/ModelConfigPage";
import ModelProviderConfigPage from "../pages/Settings/ModelProviderConfigPage";
import UsersPage from "../pages/Settings/UsersPage";
import GroupsPage from "../pages/Settings/GroupsPage";
import GoogleWorkspaceConfigPage from "../pages/Settings/GoogleWorkspaceConfigPage";
import ScimConfigPage from "../pages/Settings/ScimConfigPage";
import SsoProvidersPage from "../pages/Settings/SsoProvidersPage";
import BreakGlassPage from "../pages/Settings/BreakGlassPage";
import ScheduledJobsPage from "../pages/Settings/ScheduledJobsPage";
import AppsPage from "../pages/Settings/AppsPage";
import AppDetailPage from "../pages/Settings/AppDetailPage";
import AuditExportsPage from "../pages/Settings/AuditExportsPage";
import FinOpsPage from "../pages/Settings/FinOpsPage";
import AuditLogPage from "../pages/Settings/AuditLogPage";
import SettingsLandingPage from "../pages/Settings/SettingsLandingPage";
import ProfilePage from "../pages/Settings/ProfilePage";
import ForgeProvidersPage from "../pages/Settings/ForgeProvidersPage";
import IdentityLayout, { IdentityIndexRedirect } from "../pages/Settings/IdentityLayout";
import ConfigLayout, { ConfigIndexRedirect } from "../pages/Settings/ConfigLayout";
import ConfigGeneralPage from "../pages/Settings/ConfigGeneralPage";
import ConfigAuthPage from "../pages/Settings/ConfigAuthPage";
import ConfigEmailPage from "../pages/Settings/ConfigEmailPage";
import ConfigSchedulerPage from "../pages/Settings/ConfigSchedulerPage";
import ConfigGluonPage from "../pages/Settings/ConfigGluonPage";
import ConfigSandboxPage from "../pages/Settings/ConfigSandboxPage";
import ConfigRealtimePage from "../pages/Settings/ConfigRealtimePage";
import ConfigDesktopPage from "../pages/Settings/ConfigDesktopPage";
import DesktopPage from "../pages/Settings/DesktopPage";
import ConfigMediaPage from "../pages/Settings/ConfigMediaPage";
import ConfigStoragePage from "../pages/Settings/ConfigStoragePage";
import ConfigLoggingPage from "../pages/Settings/ConfigLoggingPage";
import ConfigCachePage from "../pages/Settings/ConfigCachePage";
import ConfigTelemetryPage from "../pages/Settings/ConfigTelemetryPage";

const PrivacyPolicy = lazy(() => import("../pages/PrivacyPolicy"));

const AppRouter = () => {
  return (
    <BrowserRouter
      future={{
        v7_relativeSplatPath: true,
        v7_startTransition: true,
      }}
    >
      <Routes>
        <Route path={ROUTES.INVITE} element={<HandleInvitation />} />
        <Route path={ROUTES.AUTH_ACTION} element={<ActionPage />} />
        <Route
          path={ROUTES.PRIVACY_POLICY}
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <PrivacyPolicy />
            </Suspense>
          }
        />

        <Route element={<CheckLogin />}>
          <Route path={ROUTES.LOGIN} element={<Login />} />

          <Route element={<ProtectedRoute />}>
            <Route path={ROUTES.BROWSER_AUTHORIZE} element={<BrowserAuthorizationPage />} />
            <Route element={<AppLayout />}>
              <Route path={ROUTES.CHAT.ROOT} element={<Chat />} />
              <Route path={ROUTES.CHAT.BY_ID} element={<Chat />} />

              <Route path={ROUTES.SPECTRA.ROOT} element={<SpectraDash />} />
              <Route path={ROUTES.SPECTRA.CREATE} element={<CreateSpectraPage />} />
              <Route path={ROUTES.SPECTRA.EDIT} element={<EditSpectraPage />} />
              <Route path={ROUTES.SPECTRA.LANDING} element={<SpectraLandingPage />} />
              <Route path={ROUTES.SPECTRA.CHAT} element={<SpectraChatPage />} />
              <Route
                path={ROUTES.SPECTRA.CHAT_BY_ID}
                element={<SpectraChatPage />}
              />

              {/* Forge Routes */}
              <Route path={ROUTES.FORGE.ROOT} element={<ForgeDashboard />} />
              <Route path={ROUTES.FORGE.ADD_PROVIDER} element={<AddProviderPage />} />
              <Route path={ROUTES.FORGE.ADD_AGENT} element={<AddAgentPage />} />
              <Route path={ROUTES.FORGE.EDIT_PROVIDER} element={<EditProviderPage />} />
              <Route path={ROUTES.FORGE.EDIT_AGENT} element={<EditAgentPage />} />
              {/* Sparks Dashboard Route */}
              <Route path={ROUTES.SPARKS_DASH} element={<SparksDash />} />

              {/* Settings — landing */}
              <Route path={ROUTES.SETTINGS.ROOT} element={<SettingsLandingPage />} />
              <Route path={ROUTES.SETTINGS.PROFILE} element={<ProfilePage />} />

              {/* Settings — flat pages */}
              {/* Settings — Model Garden sub-section */}
              <Route path={ROUTES.SETTINGS.MODELS} element={<ModelGardenLayout />}>
                <Route index element={<ModelGardenIndexRedirect />} />
                <Route path="catalog" element={<ModelListingPage />} />
                <Route path="config" element={<ModelConfigPage />} />
                <Route path="providers" element={<ModelProviderConfigPage />} />
              </Route>
              <Route path={ROUTES.SETTINGS.USERS} element={<UsersPage />} />
              <Route path={ROUTES.SETTINGS.GROUPS} element={<GroupsPage />} />
              <Route path={ROUTES.SETTINGS.SCHEDULED_JOBS} element={<ScheduledJobsPage />} />
              <Route path={ROUTES.SETTINGS.APPS} element={<AppsPage />} />
              <Route path="/settings/apps/:clientId" element={<AppDetailPage />} />
              <Route path={ROUTES.SETTINGS.AUDIT_EXPORTS} element={<AuditExportsPage />} />
              <Route path={ROUTES.SETTINGS.FINOPS} element={<FinOpsPage />} />
              <Route path={ROUTES.SETTINGS.AUDIT_LOG} element={<AuditLogPage />} />
              <Route path={ROUTES.SETTINGS.DESKTOP} element={<DesktopPage />} />
              <Route
                path={ROUTES.SETTINGS.DESKTOP_BRIDGE_LEGACY}
                element={<Navigate to={ROUTES.SETTINGS.DESKTOP} replace />}
              />
              <Route path={ROUTES.SETTINGS.FORGE_PROVIDERS} element={<ForgeProvidersPage />} />
              <Route path={ROUTES.SETTINGS.FORGE_PROVIDER_ADD} element={<AddProviderPage settingsMode />} />
              <Route path="/settings/forge-providers/:providerId" element={<EditProviderPage settingsMode />} />

              {/* Settings — Identity sub-section */}
              <Route path={ROUTES.SETTINGS.IDENTITY} element={<IdentityLayout />}>
                <Route index element={<IdentityIndexRedirect />} />
                <Route path="sso" element={<SsoProvidersPage />} />
                <Route path="break-glass" element={<BreakGlassPage />} />
                <Route path="directory" element={<GoogleWorkspaceConfigPage />} />
                <Route path="scim" element={<ScimConfigPage />} />
              </Route>

              {/* Settings — Config sub-section */}
              <Route path={ROUTES.SETTINGS.CONFIG} element={<ConfigLayout />}>
                <Route index element={<ConfigIndexRedirect />} />
                <Route path="general" element={<ConfigGeneralPage />} />
                <Route path="auth" element={<ConfigAuthPage />} />
                <Route path="email" element={<ConfigEmailPage />} />
                <Route path="scheduler" element={<ConfigSchedulerPage />} />
                <Route path="gluon" element={<ConfigGluonPage />} />
                <Route path="sandbox" element={<ConfigSandboxPage />} />
                <Route path="realtime" element={<ConfigRealtimePage />} />
                <Route path="desktop" element={<ConfigDesktopPage />} />
                <Route path="media" element={<ConfigMediaPage />} />
                <Route path="storage" element={<ConfigStoragePage />} />
                <Route path="logging" element={<ConfigLoggingPage />} />
                <Route path="cache" element={<ConfigCachePage />} />
                <Route path="telemetry" element={<ConfigTelemetryPage />} />
              </Route>

              {/* Legacy redirects for old Identity paths */}
              <Route path={ROUTES.SETTINGS.SSO_PROVIDERS} element={<Navigate to={ROUTES.SETTINGS.IDENTITY_SSO} replace />} />
              <Route path={ROUTES.SETTINGS.GW_CONFIG} element={<Navigate to={ROUTES.SETTINGS.IDENTITY_DIRECTORY} replace />} />
              <Route path={ROUTES.SETTINGS.SCIM_CONFIG} element={<Navigate to={ROUTES.SETTINGS.IDENTITY_SCIM} replace />} />
              <Route path={ROUTES.SETTINGS.FORGE_AGENTS_LEGACY} element={<Navigate to={ROUTES.SETTINGS.FORGE_PROVIDERS} replace />} />

              {/* Legacy redirect for admin scheduled jobs */}
              <Route path={ROUTES.SETTINGS.ADMIN_SCHEDULED_JOBS} element={<Navigate to={ROUTES.SETTINGS.SCHEDULED_JOBS} replace />} />
            </Route>
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export default AppRouter;
